import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { CalculatedOdds, OddsFormatterService } from '@frontend/sports/odds/feature';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, map } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { getLegsCount } from '../../../core/utils';
import { BetslipGeneratorContextType } from '../../bet-generation/models';
import BetslipPickActions from '../../picks/actions';
import BetslipPickUtilsService from '../../picks/services/betslip-pick-utils.service';
import { IDigitalSummaryViewModel } from '../../summary/components/digital/digital-summary-view.model';
import { BetslipSummaryService } from '../../summary/services/betslip-summary.service';
import { EntainUiBetBuilderPickRemovalTracking } from '../../tracking/tracking.models';
import { IComboBetState } from '../state';

@Component({
    selector: 'bs-digital-combo-bet-header',
    templateUrl: './combo-bet-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./combo-bet-header.component.scss'],
    standalone: false,
})
export default class ComboBetHeaderComponent implements OnInit {
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() comboBetState: IComboBetState;
    @Input() pickList: BetslipPick[];
    infoHeader: string;
    infoHeaderTitle: string;
    infoMessage: string;

    showBetbuilderInfoMessage = false;
    vm$: Observable<{
        summary: IDigitalSummaryViewModel;
        messages: {
            title: string;
            subTitle: string;
        };
    }>;

    private isLinear$: Observable<boolean>;

    constructor(
        private store: Store,
        private summaryService: BetslipSummaryService,
        private oddsFormatter: OddsFormatterService,
        private trackingService: TrackingService,
        private betslipPickUtilsService: BetslipPickUtilsService,
        public sitecore: Sitecore,
        private interpolationPipe: StringInterpolationPipe,
    ) {
        this.isLinear$ = store.select(selectIsLinearBetslip);
        this.infoHeader = this.sitecore.betslip.messages.BetbuilderComboInfoHeader;
        this.infoHeaderTitle = this.sitecore.betslip.messages.BetbuilderComboHeaderTitle;
        this.infoMessage = this.sitecore.betslip.messages.BetbuilderComboInfoText;
    }

    ngOnInit(): void {
        this.vm$ = combineLatest([this.isLinear$, this.summaryService.getSummaryViewModel(BetslipGeneratorContextType.Betslip)]).pipe(
            map(([isLinear, { betslip, summary, vm }]) => {
                const selectedPicksIds = Object.keys(this.comboBetState.picks).filter(
                    (p) => this.comboBetState.picks[p].isSelected && (!isLinear || !this.comboBetState.picks[p].isLocked),
                );
                const filteredStatePicks = this.pickList.filter((p) => selectedPicksIds.indexOf(p.id.toString()) > -1);

                return {
                    summary: vm,
                    messages: {
                        title: this.sitecore.betslip.messages.BetbuilderComboHeaderTitle,
                        subTitle: this.interpolationPipe.transform(this.sitecore.betslip.messages.BetbuilderComboHeaderSubTitle, {
                            legsCount: getLegsCount(filteredStatePicks),
                        }),
                    },
                };
            }),
        );
    }

    formattedOdds(odds: CalculatedOdds | null): string {
        return this.oddsFormatter.format(odds, { displayFormat: this.oddsDisplayFormat, isSummary: true });
    }

    openBetbuilderInfoMessage(): void {
        this.showBetbuilderInfoMessage = true;
    }

    closeBetbuilderInfoMessage(): void {
        this.showBetbuilderInfoMessage = false;
    }

    removeAllPick() {
        this.trackActions('confirm delete all', NOT_APPLICABLE);
        this.betslipPickUtilsService.trackEntainUiBetBuilderPickRemoval(EntainUiBetBuilderPickRemovalTracking.TrackingDeleteConfirmation);

        const pickIds = this.pickList.map((pick) => pick.id);

        this.store.dispatch(BetslipPickActions.requestRemoveMultiplePicks({ pickIds }));
    }

    private trackActions(labelEvent: string, eventDetails: string) {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'betslip',
            [trackingConstants.COMPONENT_LABEL_EVENT]: labelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }
}
